import { ART_PAL, body, fade, mix, n, shade, tint, vgrad, warm, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Mahjong Tables scene behind the game zone: a quiet tea house — dark wood lattice windows (窗格) at both edges that
 * fade out towards the middle, a soft lamp light over the table and, on the wide scene only, two low still lifes in the
 * free band under the own seat: a low bamboo sprig at the bottom left (under the disclaimer) and a teapot with two cups at the bottom
 * right. The table, the own seat and the tournament bar cover almost the whole game zone, so the scene is kept very
 * restrained: low contrast, nothing tall, nothing under the panels (title row, table bar, table, own seat, the right
 * rail). The phone has no free band at all, so its scene is the lattice and the light only. No filters, no text;
 * no groups of four (Chinese taboo, docs/art-prompts.md). Motifs are marked `data-motif="<x>"` (tests keep them out of
 * the middle 40%).
 */
export function mahjongTableScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `mt${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;

  const wood = P.dark ? '#7A5334' : '#9A6B45';
  const top = P.dark ? mix(P.bg, P.green, 0.07) : mix(P.green, '#FFFFFF', 0.92);
  const mid = P.dark ? mix(P.bg, P.teal, 0.05) : mix(P.teal, '#FFFFFF', 0.94);
  const bottom = P.dark ? mix(P.bg, wood, 0.12) : mix(P.gold, '#FFFFFF', 0.86);
  const jade = P.dark ? mix(P.green, P.bg, 0.4) : shade(P.green, 0.05);
  const clay = P.dark ? mix(P.orange, '#5A2E1E', 0.55) : mix(P.orange, '#8A4A2E', 0.45);
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  // lattice: a window grid tile (square with a centred square turned on its side), faded by a mask
  const T = wide ? 44 : 30;
  const k = T / 44;
  const tile =
    `<pattern id="${p}lt" width="${T}" height="${T}" patternUnits="userSpaceOnUse">` +
    `<path d="M0 0H${T}V${T}M${n(22 * k)} ${n(10 * k)}L${n(34 * k)} ${n(22 * k)}L${n(22 * k)} ${n(34 * k)}L${n(10 * k)} ${n(22 * k)}Z" fill="none" stroke="${wood}" stroke-width="${n(2.2 * k + 0.4)}"/></pattern>`;
  const band = wide ? 300 : 56;
  let defs =
    `${vgrad(`${p}bg`, top, mid, bottom)}${tile}` +
    `<linearGradient id="${p}fl" x1="0" x2="1"><stop offset="0" stop-color="#FFF" stop-opacity=".9"/><stop offset="1" stop-color="#FFF" stop-opacity="0"/></linearGradient>` +
    `<linearGradient id="${p}fr" x1="1" x2="0"><stop offset="0" stop-color="#FFF" stop-opacity=".9"/><stop offset="1" stop-color="#FFF" stop-opacity="0"/></linearGradient>` +
    `<mask id="${p}ml"><rect width="${band}" height="${H}" fill="url(#${p}fl)"/></mask>` +
    `<mask id="${p}mr"><rect x="${W - band}" width="${band}" height="${H}" fill="url(#${p}fr)"/></mask>` +
    fade(`${p}pool`, warm(P), P.dark ? 0.1 : 0.16) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.3 : 0.4);
  if (wide) defs += body(`${p}pot`, clay, 0.3, 0.3, 0.9, 0.9);

  /** a low bamboo sprig: short cut stalks with a node rising from the bottom edge, leaves spreading sideways */
  const bamboo = (x: number) => {
    const stalk = (sx: number, h: number, w: number) =>
      `<rect x="${n(sx - w / 2)}" y="${H - h}" width="${w}" height="${h}" rx="${n(w / 2)}"/>` +
      `<rect x="${n(sx - w / 2 - 1.5)}" y="${H - h + 14}" width="${w + 3}" height="3" rx="1.5" fill="${shade(jade, 0.25)}"/>`;
    const leafAt = (lx: number, ly: number, rot: number, len: number) =>
      `<path d="M${n(lx)} ${n(ly)}q${n(len * 0.45)} ${n(-len * 0.16)} ${n(len)} 0q${n(-len * 0.55)} ${n(len * 0.16)} ${n(-len)} 0Z" transform="rotate(${rot} ${n(lx)} ${n(ly)})"/>`;
    return (
      `<g data-motif="${Math.round(x)}" fill="${jade}" fill-opacity="${o(0.6, 0.7)}">` +
      stalk(x, 52, 9) +
      stalk(x + 24, 38, 7) +
      stalk(x - 22, 28, 6) +
      leafAt(x + 4, H - 44, -14, 62) +
      leafAt(x + 6, H - 38, 8, 50) +
      leafAt(x + 26, H - 30, -4, 44) +
      leafAt(x - 4, H - 42, 192, 56) +
      leafAt(x - 20, H - 24, 172, 46) +
      `</g>`
    );
  };

  /** a round clay teapot with a lid and a curved spout, two small cups beside it */
  const teaset = (x: number, y: number) => {
    const cup = (cx: number) =>
      `<path d="M${n(cx - 15)} ${n(y + 18)}h30l-4 20h-22Z" fill="${P.dark ? mix(P.ink, P.bg, 0.55) : '#FFFFFF'}" stroke="${jade}" stroke-width="1.5"/>`;
    return (
      `<g data-motif="${Math.round(x)}" opacity="${o(0.7, 0.72)}">` +
      `<ellipse cx="${n(x + 20)}" cy="${n(y + 40)}" rx="110" ry="9" fill="${P.dark ? '#000' : shade(P.ink, 0.2)}" fill-opacity="${o(0.3, 0.08)}"/>` +
      `<path d="M${n(x + 30)} ${n(y + 2)}q26 -2 34 -30" fill="none" stroke="${clay}" stroke-width="7" stroke-linecap="round"/>` +
      `<path d="M${n(x - 30)} ${n(y - 16)}q-26 4 -12 30" fill="none" stroke="${shade(clay, 0.15)}" stroke-width="6"/>` +
      `<ellipse cx="${n(x)}" cy="${n(y + 6)}" rx="36" ry="32" fill="url(#${p}pot)"/>` +
      `<ellipse cx="${n(x)}" cy="${n(y - 25)}" rx="18" ry="5" fill="${shade(clay, 0.2)}"/>` +
      `<circle cx="${n(x)}" cy="${n(y - 32)}" r="4.5" fill="${shade(clay, 0.2)}"/>` +
      `<ellipse cx="${n(x - 13)}" cy="${n(y - 6)}" rx="8" ry="13" transform="rotate(30 ${n(x - 13)} ${n(y - 6)})" fill="#FFFFFF" fill-opacity="${o(0.12, 0.3)}"/>` +
      cup(x + 76) +
      cup(x + 118) +
      `</g>`
    );
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<g opacity="${o(0.12, 0.13)}"><rect width="${band}" height="${H}" fill="url(#${p}lt)" mask="url(#${p}ml)"/>` +
    `<rect x="${W - band}" width="${band}" height="${H}" fill="url(#${p}lt)" mask="url(#${p}mr)"/></g>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.1)}" rx="${n(W * 0.38)}" ry="${n(H * 0.5)}" fill="url(#${p}pool)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.5)}" rx="${n(W * 0.34)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  // the wide scene is cropped to the game zone (cover): at 1440 × 900 about x 205…1395 of it is seen; the table and the
  // own seat cover x ≈ 225…1375 down to y ≈ 720 (≈ 760 with the tournament bar), the disclaimer sits under them on the
  // left — only the band at the very bottom is free
  if (wide) art += bamboo(300) + teaset(1200, 846);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    `<defs>${defs}</defs>` +
    art +
    `</svg>`
  );
}

/** Header accent of Mahjong Tables: a thin bamboo line with a node per tile (repeat-x tile, 16 × 8). */
export function mahjongTableAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const jade = P.dark ? tint(P.green, 0.1) : shade(P.green, 0.05);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8" width="16" height="8">` +
    `<path d="M0 6H16" stroke="${jade}" stroke-width="2.4" stroke-opacity="${P.dark ? 0.55 : 0.7}"/>` +
    `<path d="M15 4.2V7.8" stroke="${warm(P)}" stroke-opacity="${P.dark ? 0.7 : 0.85}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'mahjong-table', scene: mahjongTableScene, accent: mahjongTableAccent });
